<template>
  <div class="dashboard-page">
    <!-- Quick Actions (First Position) -->
    <div class="quick-actions-section">
      <div class="quick-actions-wrapper" :class="{ 'customizing': customizationStore.isCustomizing }">
        <div class="quick-actions-header">
          <div class="header-left">
            <el-icon :size="18" style="color: rgba(255, 255, 255, 0.9);">
              <Lightning />
            </el-icon>
            <span class="quick-actions-title">{{ $t('dashboard.quickActions') }}</span>
          </div>
          <div class="header-right">
            <el-button v-if="!customizationStore.isCustomizing" type="primary" size="small" @click="enterCustomizeMode"
              :icon="Setting">
              {{ $t('dashboard.customize') }}
            </el-button>
            <template v-else>
              <el-button type="danger" size="small" @click="resetCustomization" :icon="RefreshRight">
                {{ $t('dashboard.resetLayout') }}
              </el-button>
              <el-button type="info" size="small" @click="cancelCustomization">
                {{ $t('dashboard.cancelCustomize') }}
              </el-button>
              <el-button type="success" size="small" @click="saveCustomization" :icon="Check">
                {{ $t('dashboard.saveLayout') }}
              </el-button>
            </template>
          </div>
        </div>
        <div class="quick-actions" ref="quickActionsRef">
          <div v-for="action in quickActionItems" :key="action.id"
            v-show="customizationStore.isCustomizing || action.visible" class="quick-action-item"
            :class="{ 'dimmed': !action.visible && customizationStore.isCustomizing, 'customizing': customizationStore.isCustomizing }"
            :data-action-id="action.id" @click="!customizationStore.isCustomizing && handleQuickAction(action.path)">
            <div class="quick-action-icon" :style="{ background: action.bgColor, color: action.color }">
              <i v-if="action.iconfont" :class="`iconfont ${action.iconfont}`"
                :style="{ fontSize: '24px', color: action.color }"></i>
              <el-icon v-else :size="24" :style="{ color: action.color }">
                <component :is="action.icon" />
              </el-icon>
            </div>
            <span>{{ action.label }}</span>

            <!-- Customization Controls -->
            <div v-if="customizationStore.isCustomizing" class="customization-controls">
              <el-checkbox v-model="action.visible" @change="toggleQuickAction(action.id)" class="visibility-toggle" />
              <div class="drag-handle">
                <el-icon :size="16">
                  <Rank />
                </el-icon>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="stats-grid">
      <el-row :gutter="12">
        <el-col v-for="stat in statistics" v-show="isStatVisible(stat.key) || customizationStore.isCustomizing"
          :key="stat.key" :xs="8" :sm="6" :md="6" :lg="4" :xl="3" class="stat-col">
          <el-card class="stat-card"
            :class="['stat-card-' + stat.key, { 'customizing': customizationStore.isCustomizing, 'dimmed': !isStatVisible(stat.key) && customizationStore.isCustomizing }]"
            shadow="hover" @click="!customizationStore.isCustomizing && handleStatClick(stat.key)"
            :style="{ cursor: customizationStore.isCustomizing ? 'move' : 'pointer' }" :data-stat-key="stat.key">
            <div class="stat-content">
              <div class="stat-icon-wrapper" :style="{ backgroundColor: stat.bgColor }">
                <i v-if="stat.iconfont" :class="`iconfont ${stat.iconfont}`"
                  :style="{ fontSize: '24px', color: stat.color }"></i>
                <el-icon v-else :size="24" :style="{ color: stat.color }">
                  <component :is="stat.icon" />
                </el-icon>
              </div>
              <div class="stat-info">
                <div class="stat-value">{{ stat.value }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>

            <!-- Customization Controls -->
            <div v-if="customizationStore.isCustomizing" class="stat-customization-controls">
              <el-checkbox :model-value="isStatVisible(stat.key)" @change="toggleStatistic(stat.key)"
                class="stat-visibility-toggle" />
              <div class="stat-drag-handle">
                <el-icon :size="16">
                  <Rank />
                </el-icon>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- Main Content Grid -->
    <el-row :gutter="20" class="main-content-grid">
      <!-- Recent Bookings -->
      <el-col :xs="24">
        <el-card class="content-card" shadow="never">
          <template #header>
            <div class="card-header">
              <span class="card-title">
                <el-icon>
                  <Document />
                </el-icon>
                {{ $t('dashboard.recentBookings') }}
              </span>
              <div style="display: flex; align-items: center; gap: 8px;">
                <!-- View mode toggle -->
                <el-button-group>
                  <el-button 
                    :type="bookingViewMode === 'list' ? 'primary' : 'default'" 
                    size="small"
                    @click="bookingViewMode = 'list'"
                  >
                    <el-icon><List /></el-icon>
                  </el-button>
                  <el-button 
                    :type="bookingViewMode === 'card' ? 'primary' : 'default'" 
                    size="small"
                    @click="bookingViewMode = 'card'"
                  >
                    <el-icon><Grid /></el-icon>
                  </el-button>
                </el-button-group>
                
                <el-button type="primary" link @click="goToBookings">
                  {{ $t('dashboard.viewAll') }}
                  <el-icon class="el-icon--right">
                    <ArrowRight />
                  </el-icon>
                </el-button>
              </div>
            </div>
          </template>

          <!-- List View -->
          <div v-if="bookingViewMode === 'list'" class="booking-grouped-list">
            <div v-for="dateGroup in recentBookings" :key="dateGroup.date" class="date-group">
              <div class="date-group-header">
                <el-icon :size="14" style="color: var(--color-primary, #409EFF);">
                  <Calendar />
                </el-icon>
                <span>{{ dateGroup.date }}</span>
              </div>
              <el-table 
                :data="dateGroup.timeSlots" 
                style="width: 100%"
                :show-header="true"
                :header-cell-style="{ background: 'var(--bg-color-page-light, #f8f9fa)', color: 'var(--color-text-primary, #303133)', fontWeight: '600', fontSize: '13px' }"
                :row-style="{ height: 'auto' }" 
                :cell-style="{ padding: '10px 0', fontSize: '13px' }" 
                stripe
              >
                <el-table-column prop="timeStr" :label="$t('dashboard.time')" width="90" align="center">
                  <template #default="{ row }">
                    <span 
                      style="
                        background: var(--color-primary, #409EFF);
                        color: #fff;
                        padding: 4px 10px;
                        border-radius: 6px;
                        font-size: 13px;
                        font-weight: 600;
                        box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
                        display: inline-block;
                      "
                    >
                      {{ row.timeStr }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('dashboard.teacher') + ' / ' + $t('dashboard.customer')">
                  <template #default="{ row }">
                    <div class="booking-list">
                      <div v-for="b in row.bookings" :key="b.id" class="booking-list-item">
                        <div class="booking-teacher">
                          <i class="iconfont icon-teacher" style="font-size: 13px; color: var(--color-primary, #409EFF);"></i>
                          <span>{{ b.teacherName }}</span>
                        </div>
                        <div class="booking-customer">
                          <i class="iconfont icon-helmet" style="font-size: 13px; color: #E6A23C;"></i>
                          <span class="dashboard-customer-link" @click="showCustomerInfo(b.customerId, b.customerName)">
                            {{ b.customerName }}
                          </span>
                        </div>
                        <div class="booking-status">
                          <el-icon :size="12" :style="{ color: getStatusColor(b.status) }">
                            <component :is="getStatusIcon(b.status)" />
                          </el-icon>
                          <span style="font-size: 12px; font-weight: 500;">
                            {{ getStatusLabel(b.status) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>

          <!-- Card View -->
          <div v-else class="booking-cards-container">
            <div 
              v-for="dateGroup in recentBookings" 
              :key="dateGroup.date" 
              class="date-group-card"
            >
              <div class="date-group-card-header">
                <el-icon :size="16" style="color: var(--color-primary, #409EFF);">
                  <Calendar />
                </el-icon>
                <span>{{ dateGroup.date }}</span>
              </div>
              <div 
                v-for="slot in dateGroup.timeSlots" 
                :key="slot.timeStr"
                class="booking-card-item"
              >
                <div class="booking-card-header">
                  <div class="booking-card-datetime">
                    <div class="booking-card-times">
                      <el-icon :size="16" style="color: var(--color-primary, #409EFF); font-weight: 600;">
                        <Clock />
                      </el-icon>
                      <span class="booking-hour-badge">{{ slot.timeStr }}</span>
                    </div>
                  </div>
                </div>
                
                <div class="booking-card-details">
                  <div 
                    v-for="b in slot.bookings" 
                    :key="b.id" 
                    class="booking-card-detail-row"
                  >
                    <div class="booking-card-row">
                      <i class="iconfont icon-teacher"></i>
                      <span>{{ b.teacherName }}</span>
                    </div>
                    <div class="booking-card-row">
                      <i class="iconfont icon-helmet"></i>
                      <span 
                        class="dashboard-customer-link" 
                        @click="showCustomerInfo(b.customerId, b.customerName)"
                      >
                        {{ b.customerName }}
                      </span>
                    </div>
                    <div class="booking-card-status-badge">
                      <el-icon :size="12" :style="{ color: getStatusColor(b.status) }">
                        <component :is="getStatusIcon(b.status)" />
                      </el-icon>
                      <span>{{ getStatusLabel(b.status) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <el-empty v-if="!recentBookings.length && !loading" :description="$t('dashboard.noRecentBookings')" />
        </el-card>
      </el-col>

      <!-- Customer Info Dialog -->
      <el-dialog
        v-model="showCustomerDialog"
        :title="$t('booking.customerInfo')"
        width="500px"
        destroy-on-close
      >
        <div v-loading="customerDialogLoading" class="customer-info-content">
          <div v-if="customerInfo" class="customer-info-wrapper">
            <div class="customer-avatar-section">
              <ImagePreview 
                v-if="customerInfo.avatar"
                :src="customerInfo.avatar"
                width="100px"
                height="100px"
                :round="true"
                :preview-title="customerInfo.name"
              />
              <div v-else class="avatar-placeholder">
                <i class="iconfont icon-helmet" style="font-size: 50px; color: #E6A23C;"></i>
              </div>
            </div>
            
            <el-descriptions :column="1" border class="customer-descriptions">
              <el-descriptions-item :label="$t('customer.name')">
                {{ customerInfo.name }}
              </el-descriptions-item>
              <el-descriptions-item :label="$t('customer.phone')">
                {{ customerInfo.phone || '-' }}
              </el-descriptions-item>
              <el-descriptions-item :label="$t('customer.gender')">
                {{ customerInfo.gender ? $t('customer.male') : $t('customer.female') }}
              </el-descriptions-item>
              <el-descriptions-item :label="$t('customer.age')">
                {{ customerInfo.age || '-' }}
              </el-descriptions-item>
              <el-descriptions-item :label="$t('customer.status')">
                <el-tag :type="customerInfo.status === 1 ? 'success' : 'info'" size="small">
                  {{ customerInfo.status === 1 ? $t('customer.active') : $t('customer.inactive') }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('customer.remark')">
                {{ customerInfo.remark || '-' }}
              </el-descriptions-item>
              <el-descriptions-item :label="$t('customer.totalRemainingVipHours')">
                <span class="vip-hours">{{ customerInfo.totalRemainingVipHours }}</span>
                <el-tag v-if="customerInfo.hasActiveVipPackage" type="warning" size="small" effect="dark" style="margin-left: 8px;">
                  {{ customerInfo.activeVipPackageCount }} {{ $t('customer.activeVipPackage') }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('common.createdAt')">
                {{ customerInfo.createdAt ? formatBookingDate(customerInfo.createdAt) : '-' }}
              </el-descriptions-item>
            </el-descriptions>
          </div>
          <el-empty v-else :description="$t('common.noData')" />
        </div>
        <template #footer>
          <el-button @click="showCustomerDialog = false">{{ $t('common.close') }}</el-button>
        </template>
      </el-dialog>
    </el-row>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, markRaw, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'
import { useDashboardStore } from '@/stores/dashboard'
import { useDashboardCustomizationStore } from '@/stores/dashboard-customization'
import { useCustomerStore } from '@/stores/customer'
import ImagePreview from '@/components/ImagePreview.vue'
import { getBookingList } from '@/api/booking'
import MTime from '@/utils/time-handler'
import Sortable from 'sortablejs'
import {
  Document,
  Money,
  ArrowRight,
  Lightning,
  Calendar,
  Clock,
  InfoFilled,
  Setting,
  Check,
  RefreshRight,
  Rank,
  CircleCheck,
  CircleCheckFilled,
  CircleClose,
  List,
  Grid
} from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'

const router = useRouter()
const { t } = useI18n()
const userStore = useUserStore()
const dashboardStore = useDashboardStore()
const customizationStore = useDashboardCustomizationStore()
const customerStore = useCustomerStore()

const loading = ref(false)

// Customer info dialog state
const showCustomerDialog = ref(false)
const customerDialogLoading = ref(false)
const customerInfo = ref(null)
// Auto-switch booking view mode based on screen size
const isMobileView = ref(window.innerWidth < 768)

const handleResize = () => {
  isMobileView.value = window.innerWidth < 768
  // Auto-toggle to card view on mobile
  if (isMobileView.value) {
    bookingViewMode.value = 'card'
  } else {
    bookingViewMode.value = 'list'
  }
}

const recentBookings = ref([])
const bookingViewMode = ref(window.innerWidth < 768 ? 'card' : 'list') // Auto: card on mobile, list on desktop
const quickActionsRef = ref(null)
let quickActionsSortable = null
let statisticsSortable = null

const userName = computed(() => userStore.userInfo?.name || userStore.username || 'User')

const currentDate = computed(() => {
  const now = new Date()
  return now.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
})

const statistics = ref([
  {
    key: 'todayBookings',
    label: t('dashboard.stats.todayBookingCount'),
    value: 0,
    icon: markRaw(Document),
    color: '#409EFF',
    bgColor: '#409EFF20'
  },
  {
    key: 'vipRevenue',
    label: t('dashboard.stats.vipRevenue'),
    value: 0,
    icon: markRaw(Money),
    color: '#E6A23C',
    bgColor: '#E6A23C20',
    isCurrency: true
  },
  {
    key: 'monthlyIncome',
    label: t('dashboard.monthlyIncome'),
    value: 0,
    icon: markRaw(Money),
    color: '#67C23A',
    bgColor: '#67C23A20',
    isCurrency: true
  },
  {
    key: 'customers',
    label: t('dashboard.stats.customerCount'),
    value: 0,
    iconfont: 'icon-helmet',
    color: '#409EFF',
    bgColor: '#409EFF20'
  },
  {
    key: 'teachers',
    label: t('dashboard.stats.teacherCount'),
    value: 0,
    iconfont: 'icon-teacher',
    color: '#67C23A',
    bgColor: '#67C23A20'
  },
  {
    key: 'horses',
    label: t('dashboard.stats.horseCount'),
    value: 0,
    iconfont: 'icon-horse',
    color: '#E6A23C',
    bgColor: '#E6A23C20'
  },
  {
    key: 'vipPackages',
    label: t('dashboard.stats.vipPackages'),
    value: 0,
    icon: markRaw(Money),
    color: '#F56C6C',
    bgColor: '#F56C6C20'
  }
])

const statusConfig = {
  pending: { type: 'warning', label: 'pending', icon: 'Clock', color: '#E6A23C' },
  confirmed: { type: 'success', label: 'confirmed', icon: 'CircleCheck', color: '#67C23A' },
  completed: { type: 'success', label: 'completed', icon: 'CircleCheckFilled', color: '#67C23A' },
  cancelled: { type: 'danger', label: 'cancelled', icon: 'CircleClose', color: '#F56C6C' }
}

const getStatusType = (status) => statusConfig[status]?.type || 'info'
const getStatusLabel = (status) => t(`booking.${statusConfig[status]?.label || status}`)
const getStatusIcon = (status) => statusConfig[status]?.icon || 'InfoFilled'
const getStatusColor = (status) => statusConfig[status]?.color || '#909399'

const updateStatistics = () => {
  const stats = dashboardStore.statistics
  const statMap = {
    vipRevenue: 'vipRevenue',
    monthlyIncome: 'monthlyIncome',
    customers: 'totalCustomers',
    teachers: 'totalTeachers',
    horses: 'totalHorses',
    vipPackages: 'activeVipPackageCount'
  }

  statistics.value.forEach(stat => {
    const key = statMap[stat.key]
    if (key) {
      stat.value = stats[key] || 0
    }
  })
}

// Fetch today's booking count directly from Booking API
const fetchTodayBookingsCount = async () => {
  try {
    const today = MTime.getCurrentDate()
    const selfId = userStore.userId || 1 // Get from user session, fallback to 1
    const params = {
      SelfId: selfId,
      StartDate: MTime.formatQueryStartDate(today),
      EndDate: MTime.formatQueryEndDate(today)
    }

    console.log('Fetching today bookings with params:', params)

    const res = await getBookingList(params)

    console.log('Today bookings API response:', res)

    if (res.success) {
      let count = 0
      let bookingsData = []

      if (Array.isArray(res.data)) {
        bookingsData = res.data
      } else if (res.data && typeof res.data === 'object') {
        bookingsData = res.data.list || []
      }

      // Calculate total booked hours instead of booking count
      let totalHours = 0
      bookingsData.forEach(booking => {
        booking.bookingTimes?.forEach(bookingTime => {
          totalHours += bookingTime?.bookedHours?.length || 0
        })
      })
      count = totalHours

      console.log('Processed bookings data:', bookingsData)

      // Update the todayBookings statistic
      const todayBookingStat = statistics.value.find(s => s.key === 'todayBookings')
      if (todayBookingStat) {
        todayBookingStat.value = count
      }

      // If dashboard store doesn't have recent bookings, use this data
      if (!recentBookings.value || recentBookings.value.length === 0) {
        // Sort by createdAt descending, take top bookings
        const sortedBookings = bookingsData
          .sort((a, b) => {
            const dateA = new Date(a.createdAt || 0)
            const dateB = new Date(b.createdAt || 0)
            return dateB - dateA
          })
          .slice(0, 20) // Get more to group properly

        // Group by date -> time slot
        const groupedByDate = {}
        sortedBookings.forEach(booking => {
          // Iterate over ALL bookingTimes (each booking can have multiple date entries)
          booking.bookingTimes?.forEach(bookingTime => {
            // Iterate over ALL bookedHours within each bookingTime
            bookingTime?.bookedHours?.forEach(bh => {
              const hour = bh.hour
              const timeStr = `${String(hour).padStart(2, '0')}:00`

              let dateKey = ''
              let dateStr = ''
              if (bookingTime?.bookingDate) {
                const bookingDate = new Date(bookingTime.bookingDate)
                const year = bookingDate.getFullYear()
                const month = bookingDate.getMonth() + 1
                const day = bookingDate.getDate()
                dateStr = `${year}/${month}/${day}`
                dateKey = bookingTime.bookingDate.split('T')[0] // YYYY-MM-DD
              }

              if (!groupedByDate[dateKey]) {
                groupedByDate[dateKey] = { date: dateStr, dateKey, timeSlots: {} }
              }
              if (!groupedByDate[dateKey].timeSlots[timeStr]) {
                groupedByDate[dateKey].timeSlots[timeStr] = { hour, timeStr, bookings: [] }
              }
              // Only add if this booking isn't already in this time slot (deduplicate)
              const exists = groupedByDate[dateKey].timeSlots[timeStr].bookings.some(b => b.id === booking.id)
              if (!exists) {
                groupedByDate[dateKey].timeSlots[timeStr].bookings.push({
                  id: booking.id,
                  teacherName: booking.name || booking.teacherName || '-',
                  customerName: booking.customerName || '-',
                  customerId: booking.customerId || booking.customerVipId || booking.customer?.id || null,
                  status: booking.status || 'confirmed',
                  orderNumber: booking.orderNumber,
                  createdAt: booking.createdAt
                })
              }
            })
          })
        })

        // Convert to array format
        const groupedArray = Object.values(groupedByDate)
          .sort((a, b) => a.dateKey > b.dateKey ? 1 : -1) // Sort by date
          .map(dateGroup => ({
            date: dateGroup.date,
            timeSlots: Object.values(dateGroup.timeSlots).sort((a, b) => a.hour - b.hour) // Sort by time
          }))

        recentBookings.value = groupedArray

        console.log('Grouped recent bookings for display:', recentBookings.value)
      }
    }
  } catch (error) {
    console.error('Failed to fetch today bookings count:', error)
  }
}

const fetchData = async () => {
  try {
    loading.value = true
    // Force refresh to get latest data
    await dashboardStore.fetchDashboardData(true)
    updateStatistics()
    recentBookings.value = dashboardStore.recentBookings?.slice(0, 5) || []

    console.log('Dashboard recent bookings:', recentBookings.value)

    // Fetch today's booking count directly from Booking API
    await fetchTodayBookingsCount()
  } catch (error) {
    console.error('Failed to fetch dashboard data:', error)
  } finally {
    loading.value = false
  }
}

const handleQuickAction = (path) => {
  router.push(path)
}

const handleStatClick = (key) => {
  const routes = {
    todayBookings: '/booking',
    vipRevenue: '/vip/statistics',
    monthlyIncome: '/vip/statistics',
    customers: '/customer',
    teachers: '/teacher',
    horses: '/horse',
    vipPackages: '/vip'
  }

  const targetRoute = routes[key]
  if (targetRoute) {
    router.push(targetRoute)
  }
}

const goToBookings = () => {
  router.push('/booking')
}

// Show customer info dialog
const showCustomerInfo = async (customerId, customerName = null) => {
  let id = customerId
  
  showCustomerDialog.value = true
  customerDialogLoading.value = true
  customerInfo.value = null
  
  try {
    // First try to find customer by name (most reliable since booking API may return VIP ID)
    if (customerName) {
      // If customers aren't loaded yet, fetch them
      if (customerStore.customers.length === 0) {
        await customerStore.fetchCustomers()
      }
      
      const customer = customerStore.customers.find(c => c.name === customerName)
      if (customer) {
        id = customer.id
      }
    }
    
    // If we have a valid ID, fetch customer details
    if (id && id !== null) {
      const res = await customerStore.fetchCustomerById(id)
      if (res.success) {
        customerInfo.value = res.data
      } else {
        ElMessage.warning(t('customer.notFound'))
      }
    } else {
      console.warn('No customer ID provided and could not find customer by name')
      ElMessage.warning(t('customer.notFound'))
    }
  } catch (error) {
    console.error('Failed to fetch customer info:', error)
    ElMessage.error(t('common.loadFailed'))
  } finally {
    customerDialogLoading.value = false
  }
}

// Format booking date for display
const formatBookingDate = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()
  return `${year}/${month}/${day}`
}

// Format booking time for display
const formatBookingTime = (hour) => {
  if (hour === undefined || hour === null) return '-'
  return `${String(hour).padStart(2, '0')}:00`
}

// Quick action items configuration
const quickActionItems = ref([
  {
    id: 'addBooking',
    label: t('dashboard.addBooking'),
    path: '/booking/add',
    iconfont: 'icon-rider',
    color: '#409EFF',
    bgColor: '#409EFF20',
    visible: true
  },
  {
    id: 'adminPurchase',
    label: t('dashboard.adminPurchase'),
    path: '/vip/admin-purchase',
    icon: markRaw(Money),
    color: '#67C23A',
    bgColor: '#67C23A20',
    visible: true
  },
  {
    id: 'setUnavailable',
    label: t('dashboard.setUnavailable'),
    path: '/schedule/unavailable',
    icon: markRaw(Clock),
    color: '#909399',
    bgColor: '#90939920',
    visible: true
  },
  {
    id: 'customers',
    label: t('dashboard.customers'),
    path: '/customer',
    iconfont: 'icon-helmet',
    color: '#E6A23C',
    bgColor: '#E6A23C20',
    visible: true
  },
  {
    id: 'teachers',
    label: t('dashboard.teachers'),
    path: '/teacher',
    iconfont: 'icon-teacher',
    color: '#F56C6C',
    bgColor: '#F56C6C20',
    visible: true
  }
])

// Computed property for filtered statistics
const filteredStatistics = computed(() => {
  return statistics.value.filter(stat => {
    const config = customizationStore.statisticsConfig.find(s => s.key === stat.key)
    return config ? config.visible : true
  })
})

// Check if a statistic is visible
const isStatVisible = (key) => {
  const config = customizationStore.statisticsConfig.find(s => s.key === key)
  return config ? config.visible : true
}

// Customization mode functions
const enterCustomizeMode = () => {
  customizationStore.enterCustomizeMode()
  nextTick(() => {
    initSortable()
  })
}

const saveCustomization = () => {
  customizationStore.saveToStorage()
  customizationStore.exitCustomizeMode()
  destroySortable()
  ElMessage.success(t('dashboard.layoutSaved'))
}

const cancelCustomization = () => {
  customizationStore.exitCustomizeMode()
  destroySortable()
  // Reload from storage to revert changes
  customizationStore.loadFromStorage()
}

const resetCustomization = () => {
  ElMessageBox.confirm(
    t('dashboard.resetConfirmMessage'),
    t('dashboard.resetConfirmTitle'),
    {
      confirmButtonText: t('common.confirm'),
      cancelButtonText: t('common.cancel'),
      type: 'warning'
    }
  ).then(() => {
    customizationStore.resetToDefault()
    customizationStore.exitCustomizeMode()
    destroySortable()
    ElMessage.success(t('dashboard.layoutReset'))
  }).catch(() => {
    // User cancelled, do nothing
  })
}

const toggleQuickAction = (actionId) => {
  customizationStore.toggleQuickAction(actionId)
}

const toggleStatistic = (statKey) => {
  customizationStore.toggleStatistic(statKey)
}

// Initialize SortableJS for drag-and-drop
const initSortable = () => {
  // Quick Actions Sortable
  if (quickActionsRef.value) {
    quickActionsSortable = Sortable.create(quickActionsRef.value, {
      animation: 150,
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      handle: '.drag-handle',
      onEnd: (evt) => {
        const newOrder = Array.from(evt.to.children).map(el => el.getAttribute('data-action-id'))
        customizationStore.reorderQuickActions(newOrder)
      }
    })
  }

  // Statistics Sortable
  const statsGrid = document.querySelector('.stats-grid .el-row')
  if (statsGrid) {
    statisticsSortable = Sortable.create(statsGrid, {
      animation: 150,
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      handle: '.stat-drag-handle',
      draggable: '.stat-col',
      onEnd: (evt) => {
        const newOrder = Array.from(evt.to.children).map(el => {
          const card = el.querySelector('.stat-card')
          return card ? card.getAttribute('data-stat-key') : null
        }).filter(Boolean)
        customizationStore.reorderStatistics(newOrder)
      }
    })
  }
}

const destroySortable = () => {
  if (quickActionsSortable) {
    quickActionsSortable.destroy()
    quickActionsSortable = null
  }
  if (statisticsSortable) {
    statisticsSortable.destroy()
    statisticsSortable = null
  }
}

onMounted(() => {
  fetchData()
  // Auto-toggle booking view mode on resize
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style lang="scss" scoped>
.dashboard-page {
  padding: 24px;
  background: var(--bg-color-page, #f0f2f5);
  min-height: calc(100vh - 140px);
  transition: background-color 0.3s ease;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.quick-actions-section {
  margin-bottom: 18px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }

  @media (max-width: 576px) {
    margin-bottom: 12px;
  }
}

.stats-grid {
  margin-bottom: 18px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }

  @media (max-width: 576px) {
    margin-bottom: 12px;
  }
}

.stat-col {
  margin-bottom: 8px;
}

.stat-card {
  border-radius: 8px;
  border: none;
  transition: all 0.2s ease;
  background-color: var(--bg-color-white, #fff);
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
  height: 72px;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;

  :deep(.el-card__body) {
    padding: 0 !important;
    margin: 0;
  }

  :deep(.el-card__header) {
    display: none;
  }

  @media (max-width: 768px) {
    border-radius: 6px;
    height: 68px;
  }

  @media (max-width: 576px) {
    border-radius: 6px;
    height: 64px;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(102, 126, 234, 0.08) !important;

    .stat-value {
      color: var(--color-primary, #409EFF);
    }

    .stat-icon-wrapper {
      transform: scale(1.03);
    }
  }

  .stat-content {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    width: 100%;
    box-sizing: border-box;

    @media (max-width: 768px) {
      gap: 7px;
      padding: 9px 10px;
    }

    @media (max-width: 576px) {
      gap: 6px;
      padding: 8px 9px;
    }

    .stat-icon-wrapper {
      width: 34px;
      height: 34px;
      border-radius: 7px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: transform 0.2s ease;

      @media (max-width: 768px) {
        width: 32px;
        height: 32px;
        border-radius: 6px;
      }

      @media (max-width: 576px) {
        width: 30px;
        height: 30px;
        border-radius: 6px;
      }
    }

    .stat-info {
      flex: 1;
      min-width: 0;

      .stat-value {
        font-size: 18px;
        font-weight: 600;
        color: var(--color-text-primary, #303133);
        line-height: 1.2;
        transition: color 0.2s ease;

        @media (max-width: 768px) {
          font-size: 17px;
        }

        @media (max-width: 576px) {
          font-size: 16px;
        }
      }

      .stat-label {
        font-size: 11px;
        color: var(--color-text-secondary, #909399);
        margin-top: 2px;
        font-weight: 400;
        line-height: 1.3;

        @media (max-width: 576px) {
          font-size: 10px;
        }
      }
    }
  }
}

.main-content-grid {
  .el-col {
    margin-bottom: 16px;
  }
}

.content-card {
  border-radius: 10px;
  border: none;
  background-color: var(--bg-color-white, #fff);
  transition: background-color 0.3s ease;

  :deep(.el-card__body) {
    padding: 14px;
  }

  :deep(.el-card__header) {
    padding: 12px 14px;
  }

  @media (max-width: 576px) {
    border-radius: 8px;

    :deep(.el-card__body) {
      padding: 12px;
    }

    :deep(.el-card__header) {
      padding: 10px 12px;
    }
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media (max-width: 576px) {
      flex-direction: row;
      align-items: center;
      gap: 8px;
    }

    .card-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: clamp(13px, 0.95rem, 15px);
      font-weight: 600;
      color: var(--color-text-primary, #303133);
      transition: color 0.3s ease;
    }
  }
  
  // Override global card header gradient for dashboard
  :deep(.el-card__header) {
    background: var(--bg-color-white, #fff) !important;
    border-bottom: 1px solid var(--border-color-lighter, #ebeef5);
    color: var(--color-text-primary, #303133);
    padding: 12px 14px;
  }

  // Recent Bookings Table Styling
  :deep(.el-table) {
    border-radius: 8px;
    overflow: hidden;

    .el-table__header-wrapper {
      th {
        background-color: var(--bg-color-page-light, #f8f9fa) !important;
        border-bottom: 2px solid var(--border-color-lighter, #ebeef5);
        font-weight: 600;
        color: var(--color-text-primary, #303133);
      }
    }

    .el-table__body-wrapper {
      tr {
        transition: all 0.2s ease;

        &:hover {
          background-color: var(--bg-color-page-lighter, #f0f9ff) !important;
          transform: scale(1.002);
        }

        td {
          border-bottom: 1px solid var(--border-color-lighter, #f0f0f0);
          color: var(--color-text-regular, #606266);
        }
      }

      // Stripe rows
      tr.el-table__row--striped {
        background-color: var(--bg-color-page-light, #fafafa);

        &:hover {
          background-color: var(--bg-color-page-lighter, #f0f9ff) !important;
        }
      }
    }

    // Empty state
    .el-empty {
      padding: 40px 0;

      .el-empty__description {
        color: var(--color-text-secondary, #909399);
        font-size: 13px;
      }
    }
  }
  
  // Grouped Booking List (by date)
  .booking-grouped-list {
    .date-group {
      margin-bottom: 20px;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .date-group-header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        background: var(--bg-color-page-light, #f8f9fa);
        border-radius: 8px 8px 0 0;
        font-size: 14px;
        font-weight: 600;
        color: var(--color-text-primary, #303133);
      }
      
      :deep(.el-table) {
        border-radius: 0 0 8px 8px;
      }
      
      .booking-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      
      .booking-list-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 4px 0;
        
        .booking-teacher,
        .booking-customer {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 13px;
          min-width: 0;
          flex: 1;
        }
        
        .booking-status {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;
          margin-left: auto;
        }
      }
    }
  }
  
  // Booking Cards Container
  .booking-cards-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 12px;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr; /* Single column on mobile */
      gap: 10px;
    }
    
    .booking-card-item {
      background: #fff;
      border-radius: 8px;
      padding: 16px;
      border: 1px solid #EBEEF5;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
      transition: all 0.2s ease;
      
      &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
      }
      
      .booking-card-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 12px;
        
        .booking-card-datetime {
          flex: 1;
          min-width: 0;
          
          .booking-card-date {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 4px;
            
            span {
              font-size: 13px;
              color: var(--color-text-primary, #303133);
            }
          }
          
          .booking-card-times {
            display: flex;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;
            
            .booking-card-hours {
              display: flex;
              gap: 6px;
              flex-wrap: wrap;
              
              .booking-hour-badge {
                background: var(--color-primary, #409EFF);
                color: #fff;
                padding: 4px 10px;
                border-radius: 6px;
                font-size: 12px;
                font-weight: 600;
                box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
              }
            }
          }
        }
        
        .booking-card-status {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;
          margin-left: 8px;
          
          span {
            font-size: 12px;
            font-weight: 500;
            color: var(--color-text-primary, #303133);
          }
        }
      }
      
      .booking-card-details {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding-top: 12px;
        border-top: 1px solid #EBEEF5;
        
        .booking-card-row {
          display: flex;
          align-items: center;
          gap: 6px;
          
          i {
            font-size: 14px;
            flex-shrink: 0;
          }
          
          .icon-teacher {
            color: var(--color-primary, #409EFF);
          }
          
          .icon-helmet {
            color: #E6A23C;
          }
          
          span {
            font-size: 13px;
            color: var(--color-text-primary, #303133);
            font-weight: 500;
          }
        }
      }
      
      // Grouped card layout
      .booking-card-detail-row {
        padding: 8px 0;
        border-bottom: 1px dashed #EBEEF5;
        
        &:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
        
        .booking-card-row {
          display: flex;
          align-items: center;
          gap: 6px;
          
          i {
            font-size: 14px;
            flex-shrink: 0;
          }
        }
        
        .booking-card-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px;
          border-radius: 4px;
          background: #f5f7fa;
          font-size: 11px;
          color: #606266;
          margin-top: 4px;
        }
      }
    }
    
    .date-group-card {
      .date-group-card-header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        background: var(--bg-color-page-light, #f8f9fa);
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        color: var(--color-text-primary, #303133);
        margin-bottom: 8px;
      }
    }
  }
}

.quick-actions-wrapper {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  margin-bottom: 20px;
  overflow: hidden;
}

.quick-actions-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

  .header-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .header-right {
    display: flex;
    gap: 8px;
  }

  .quick-actions-title {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
  }

  :deep(.el-icon) {
    color: rgba(255, 255, 255, 0.95);
    font-size: 18px;
  }
}

.quick-actions {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  padding: 14px 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }

  @media (max-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 12px 14px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  @media (max-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    padding: 10px 12px;
  }

  .quick-action-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 14px 8px;
    background: var(--bg-color-page-light, #f8f9fa);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 85px;

    @media (max-width: 992px) {
      padding: 12px 6px;
      min-height: 80px;
    }

    @media (max-width: 576px) {
      padding: 10px 6px;
      min-height: 70px;
      gap: 5px;
    }

    &:hover {
      background: var(--bg-color-white, #fff);
      transform: translateY(-1px);
      box-shadow: 0 3px 8px rgba(102, 126, 234, 0.1);
    }

    .quick-action-icon {
      width: 38px;
      height: 38px;
      border-radius: 9px;
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: 992px) {
        width: 34px;
        height: 34px;
        border-radius: 8px;
      }

      @media (max-width: 576px) {
        width: 30px;
        height: 30px;
        border-radius: 7px;
      }
    }

    span {
      font-size: 12px;
      font-weight: 500;
      color: var(--color-text-regular, #5a5a5a);
      text-align: center;
      line-height: 1.3;

      @media (max-width: 992px) {
        font-size: 11px;
      }

      @media (max-width: 576px) {
        font-size: 10px;
      }
    }
  }
}

@media (max-width: 576px) {
  :deep(.el-table) {
    font-size: 12px;

    .el-table__header-wrapper th {
      padding: 8px 0;
    }

    .el-table__body-wrapper td {
      padding: 8px 0;
    }
  }

  :deep(.el-card__body) {
    padding: 16px;
  }

  :deep(.el-button) {
    padding: 8px 12px;
    font-size: 12px;
  }

  :deep(.el-tag) {
    padding: 2px 8px;
    font-size: 11px;
  }
}

// Customization Mode Styles
.quick-actions-wrapper.customizing {
  .quick-action-item {
    position: relative;
    border: 2px dashed var(--color-primary, #409EFF);
    cursor: move;
    transition: all 0.2s ease;
    display: flex !important; // Ensure dimmed items are displayed

    &:hover {
      border-color: var(--color-success, #67C23A);
      background: var(--bg-color-page-lighter, #f0f9ff);
    }

    &.dimmed {
      opacity: 0.5;
      filter: grayscale(60%);
      display: flex !important; // Force display for dimmed items

      &:hover {
        opacity: 0.7;
        filter: grayscale(40%);
        background: var(--bg-color-page-lighter, #f0f9ff);
      }
    }

    .customization-controls {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      padding: 8px;
      pointer-events: none;

      .visibility-toggle {
        pointer-events: auto;
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 10;
      }

      .drag-handle {
        pointer-events: auto;
        position: absolute;
        bottom: 8px;
        left: 50%;
        transform: translateX(-50%);
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(64, 158, 255, 0.9);
        color: white;
        border-radius: 50%;
        cursor: grab;
        z-index: 10;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        transition: all 0.2s ease;

        &:hover {
          background: rgba(64, 158, 255, 1);
          transform: translateX(-50%) scale(1.1);
        }

        &:active {
          cursor: grabbing;
        }
      }
    }
  }
}

.stat-card.customizing {
  position: relative;
  border: 2px dashed var(--color-primary, #409EFF);
  cursor: move !important;
  transition: all 0.2s ease;
  display: block !important; // Ensure dimmed stats are displayed

  &:hover {
    border-color: var(--color-success, #67C23A);
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2) !important;
  }

  &.dimmed {
    opacity: 0.5;
    filter: grayscale(60%);
    display: block !important; // Force display for dimmed stats

    &:hover {
      opacity: 0.7;
      filter: grayscale(40%);
      box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2) !important;
    }
  }

  .stat-customization-controls {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    pointer-events: none;
    z-index: 10;

    .stat-visibility-toggle {
      pointer-events: auto;
      position: absolute;
      top: 8px;
      right: 8px;
    }

    .stat-drag-handle {
      pointer-events: auto;
      position: absolute;
      bottom: 8px;
      left: 50%;
      transform: translateX(-50%);
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(64, 158, 255, 0.9);
      color: white;
      border-radius: 50%;
      cursor: grab;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      transition: all 0.2s ease;

      &:hover {
        background: rgba(64, 158, 255, 1);
        transform: translateX(-50%) scale(1.1);
      }

      &:active {
        cursor: grabbing;
      }
    }
  }
}

// SortableJS ghost and drag classes
:deep(.sortable-ghost) {
  opacity: 0.4;
  background: var(--bg-color-page-lighter, #f0f9ff);
}

:deep(.sortable-chosen) {
  opacity: 0.8;
  box-shadow: 0 8px 24px rgba(64, 158, 255, 0.3);
}

:deep(.sortable-drag) {
  opacity: 0.9;
  cursor: grabbing;
}

// Dashboard Customer Link
.dashboard-customer-link {
  color: #E6A23C;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    text-decoration-thickness: 2px;
    color: #d4912f;
  }
}

// Customer Info Dialog Styles
.customer-info-content {
  padding: 16px;
}

.customer-info-wrapper {
  text-align: center;
}

.customer-avatar-section {
  margin-bottom: 20px;
  
  .avatar-placeholder {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    color: white;
  }
}

.customer-descriptions {
  margin-top: 16px;
  
  :deep(.el-descriptions__label) {
    font-weight: 600;
    color: var(--color-text-primary, #303133);
  }
  
  :deep(.el-descriptions__content) {
    color: var(--color-text-regular, #606266);
  }
}

.vip-hours {
  font-size: 18px;
  font-weight: 600;
  color: #E6A23C;
}
</style>
